import * as React from 'react';
import { observer } from 'mobx-react';

import { openWidgetTestingError } from 'shared/entities/widgetSupport';
import { Button } from 'shared/newComponents/buttons';
import { ButtonSize, ButtonColor } from 'shared/newEntities/components/Button';
import { ComponentType } from 'shared/entities/components/Component';
import Typography from 'shared/newComponents/Typography';
import {
  TypographyColor,
  TypographySize,
  TypographyType,
  TypographyWeight
} from 'shared/newEntities/components/Typography';
import { IWidgetChannelModel } from 'shared/entities/channels';
import { useRootStore } from 'stores/index';

import './WidgetTesting.modules.scss';

type Props = {
  channel: IWidgetChannelModel;
};

const WidgetTesting: React.FC<Props> = ({ channel }) => {
  const rootStore = useRootStore();
  const widgetStore = rootStore.smartbotSupportWidgetStore;
  const [isWidgetInjected, setIsWidgetInjected] = React.useState(false);
  const [isWidgetInjecting, setIsWidgetInjecting] = React.useState(false);
  const isMountedRef = React.useRef(true);

  const handleInject = React.useCallback(async () => {
    if (isWidgetInjecting || isWidgetInjected) {
      return;
    }

    setIsWidgetInjecting(true);
    const apiKey = channel.apiKey.value;

    try {
      const isStarted = await widgetStore.startTestingWidget(apiKey);

      if (isMountedRef.current) {
        setIsWidgetInjected(isStarted);
      }
    } catch {
      if (isMountedRef.current) {
        openWidgetTestingError(rootStore.appNotificationsStore);
      }
    } finally {
      if (isMountedRef.current) {
        setIsWidgetInjecting(false);
      }
    }
  }, [
    channel.apiKey.value,
    isWidgetInjected,
    isWidgetInjecting,
    rootStore,
    widgetStore
  ]);

  const handleRemove = React.useCallback(() => {
    void widgetStore.stopTestingWidget();
    setIsWidgetInjected(false);
  }, [widgetStore]);

  React.useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
      void widgetStore.stopTestingWidget();
    };
  }, [widgetStore]);

  return (
    <div styleName="widget-testing">
      <div styleName="widget-testing__info">
        <Typography
          Element="span"
          fontType={TypographyType.text}
          fontSize={TypographySize.s}
          fontWeight={TypographyWeight.normal}
          fontColor={TypographyColor.secondary}
        >
          {(t) =>
            t(
              'ChannelsModal.SelectedChannel.Content.Widget.Testing.description',
              {
                ns: 'newContainers'
              }
            )
          }
        </Typography>
      </div>
      <div styleName="widget-testing__actions">
        <Button
          type={ComponentType.button}
          size={ButtonSize.m}
          color={isWidgetInjected ? ButtonColor.lightGray : ButtonColor.blue}
          onClick={handleInject}
          disabled={isWidgetInjected || isWidgetInjecting}
          isLoading={isWidgetInjecting}
        >
          Тестировать
        </Button>
        <Button
          type={ComponentType.button}
          size={ButtonSize.m}
          color={ButtonColor.lightGray}
          onClick={handleRemove}
          disabled={!isWidgetInjected || isWidgetInjecting}
        >
          Удалить
        </Button>
      </div>
    </div>
  );
};

export default observer(WidgetTesting);
